// actions/client-portal.ts
// API actions for Client Portal deliverables
import api from "@/utils/api";

export interface ClientChatMessage {
  id: number;
  uuid: string;
  slate_id: number;
  sender_id: number;
  sender_name: string;
  sender_type: "admin" | "client";
  message: string;
  is_read_admin: boolean;
  is_read_client: boolean;
  created_at: string;
  updated_at: string;
}

export interface Deliverable {
  id: number;
  uuid: string;
  title: string;
  description: string | null;
  type: "blog" | "video";
  url: string | null;
  status: "pending" | "approved" | "needs_revision";
  delivery_month: number | null;
  delivery_year: number | null;
  created_at: string;
  updated_at: string;
  reviewer_name: string | null;
  reviewed_at: string | null;
  approved_at: string | null;
  comment_count: number;
  review_token: string | null;
}

export interface DeliverableComment {
  id: number;
  uuid: string;
  user_name: string;
  comment: string;
  created_at: string;
}

export interface DeliverableStats {
  total: number;
  pending: number;
  approved: number;
  needs_revision: number;
  unread_comment_count?: number;
}

export interface GetDeliverablesResponse {
  deliverables: Deliverable[];
  stats: DeliverableStats;
  filters: {
    month: number | null;
    year: number | null;
    type: string | null;
    status: string | null;
  };
}

export interface GetCommentsResponse {
  comments: DeliverableComment[];
  count: number;
}

export interface AvailablePeriodsResponse {
  years: number[];
  months: { value: number; label: string }[];
}

/**
 * Get all deliverables for the client
 */
export const getClientDeliverables = async (
  params?: {
    month?: number;
    year?: number;
    type?: string;
    status?: string;
  }
): Promise<GetDeliverablesResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.month) queryParams.append("month", params.month.toString());
  if (params?.year) queryParams.append("year", params.year.toString());
  if (params?.type) queryParams.append("type", params.type);
  if (params?.status) queryParams.append("status", params.status);

  const queryString = queryParams.toString();
  const url = `/client-portal/deliverables${queryString ? `?${queryString}` : ""}`;

  const response = await api.get<GetDeliverablesResponse>(url);
  return response?.data || { deliverables: [], stats: { total: 0, pending: 0, approved: 0, needs_revision: 0 }, filters: { month: null, year: null, type: null, status: null } };
};

/**
 * Get deliverable stats
 */
export const getClientDeliverableStats = async (
  params?: {
    month?: number;
    year?: number;
  }
): Promise<DeliverableStats> => {
  const queryParams = new URLSearchParams();
  if (params?.month) queryParams.append("month", params.month.toString());
  if (params?.year) queryParams.append("year", params.year.toString());

  const queryString = queryParams.toString();
  const url = `/client-portal/deliverables/stats${queryString ? `?${queryString}` : ""}`;

  const response = await api.get<DeliverableStats>(url);
  return response?.data || { total: 0, pending: 0, approved: 0, needs_revision: 0 };
};

/**
 * Get a single deliverable by ID
 */
export const getClientDeliverableById = async (
  id: number
): Promise<Deliverable | null> => {
  const response = await api.get<{ deliverable: Deliverable }>(`/client-portal/deliverables/${id}`);
  return response?.data?.deliverable || null;
};

/**
 * Get comments for a deliverable
 */
export const getDeliverableComments = async (
  deliverableId: number
): Promise<GetCommentsResponse> => {
  const response = await api.get<GetCommentsResponse>(`/client-portal/deliverables/${deliverableId}/comments`);
  return response?.data || { comments: [], count: 0 };
};

/**
 * Add a comment to a deliverable
 */
export const addDeliverableComment = async (
  deliverableId: number,
  comment: string
): Promise<DeliverableComment | null> => {
  const response = await api.post<{ comment: DeliverableComment }>(`/client-portal/deliverables/${deliverableId}/comments`, {
    comment,
  });
  return response?.data?.comment || null;
};

/**
 * Get available periods for filter dropdown
 */
export const getAvailablePeriods = async (): Promise<AvailablePeriodsResponse> => {
  const response = await api.get<AvailablePeriodsResponse>(`/client-portal/deliverables/available-periods`);
  return response?.data || { years: [], months: [] };
};

// =============================================
// Client Queries API
// =============================================

export interface QueryAttachment {
  url: string;
  name: string;
  size?: number | null;
  type?: string | null;
}

export interface QueryCategoryOption {
  id: number;
  name: string;
}

export interface ClientQuery {
  id: number;
  uuid: string;
  subject: string;
  message: string;
  status: "open" | "in_progress" | "resolved";
  category?: QueryCategoryOption | null;
  attachments?: QueryAttachment[];
  created_by_name?: string;
  resolved_by_name?: string;
  resolved_at?: string;
  admin_notes?: string;
  reopen_comment?: string;
  reopened_at?: string;
  created_at: string;
}

export interface ClientQueryStats {
  total: number;
  open: number;
  in_progress: number;
  resolved: number;
}

/**
 * Get all queries for the client's school
 */
export const getClientQueries = async (
  status?: string
): Promise<{ queries: ClientQuery[]; stats: ClientQueryStats }> => {
  try {
    const params = new URLSearchParams();
    if (status && status !== "all") params.append("status", status);
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<{
      queries: ClientQuery[];
      stats: ClientQueryStats;
    }>(`/client-portal/queries${query}`);
    return {
      queries: response?.data?.queries || [],
      stats: response?.data?.stats || { total: 0, open: 0, in_progress: 0, resolved: 0 },
    };
  } catch (error) {
    console.error("Error fetching client queries:", error);
    return {
      queries: [],
      stats: { total: 0, open: 0, in_progress: 0, resolved: 0 },
    };
  }
};

/**
 * Categories a client can pick when raising a query
 */
export const getClientQueryCategories = async (): Promise<QueryCategoryOption[]> => {
  try {
    const response = await api.get<{ categories: QueryCategoryOption[] }>("/client-portal/query-categories");
    return response?.data?.categories || [];
  } catch (error) {
    console.error("Error fetching query categories:", error);
    return [];
  }
};

/**
 * Upload files for a query or comment; returns attachment objects to send with it
 */
export const uploadQueryAttachments = async (
  files: File[],
  endpoint = "/client-portal/queries/attachments"
): Promise<{ success: boolean; attachments: QueryAttachment[]; message?: string }> => {
  try {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    const response = await api.post<{ attachments: QueryAttachment[] }>(endpoint, form);
    return { success: response?.success ?? true, attachments: response?.data?.attachments || [] };
  } catch (error: any) {
    console.error("Error uploading attachments:", error);
    return { success: false, attachments: [], message: error?.message || "Failed to upload files" };
  }
};

/**
 * Create a new query
 */
export const createClientQuery = async (data: {
  category_id: number;
  subject: string;
  message: string;
  attachments?: QueryAttachment[];
}): Promise<{ success: boolean; query?: ClientQuery; message?: string }> => {
  try {
    const response = await api.post<{ query: ClientQuery }>("/client-portal/queries", data);
    return {
      success: response?.success ?? true,
      query: response?.data?.query,
      message: response?.message,
    };
  } catch (error: any) {
    console.error("Error creating query:", error);
    return {
      success: false,
      message: error?.message || "Failed to submit query",
    };
  }
};

/**
 * Get a single query by ID
 */
export const getClientQueryById = async (id: number): Promise<ClientQuery | null> => {
  try {
    const response = await api.get<{ query: ClientQuery }>(`/client-portal/queries/${id}`);
    return response?.data?.query || null;
  } catch (error) {
    console.error("Error fetching query:", error);
    return null;
  }
};

/**
 * Reopen a resolved query with a comment
 */
export const reopenClientQuery = async (
  queryId: number,
  comment: string
): Promise<{ success: boolean; query?: ClientQuery; message?: string }> => {
  try {
    const response = await api.post<{ query: ClientQuery }>(
      `/client-portal/queries/${queryId}/reopen`,
      { comment }
    );
    return {
      success: response?.success ?? true,
      query: response?.data?.query,
      message: response?.message,
    };
  } catch (error: any) {
    console.error("Error reopening query:", error);
    return {
      success: false,
      message: error?.message || "Failed to reopen query",
    };
  }
};

// =============================================
// Query Comments API
// =============================================

export interface QueryComment {
  id: number;
  uuid: string;
  user_name: string;
  user_type: "client" | "admin";
  comment: string;
  attachments?: QueryAttachment[];
  created_at: string;
}

/**
 * Get comments for a query
 */
export const getQueryComments = async (
  queryId: number
): Promise<{ comments: QueryComment[]; count: number }> => {
  try {
    const response = await api.get<{ comments: QueryComment[]; count: number }>(
      `/client-portal/queries/${queryId}/comments`
    );
    return {
      comments: response?.data?.comments || [],
      count: response?.data?.count || 0,
    };
  } catch (error) {
    console.error("Error fetching query comments:", error);
    return { comments: [], count: 0 };
  }
};

/**
 * Add a comment to a query
 */
export const addQueryComment = async (
  queryId: number,
  comment: string,
  attachments: QueryAttachment[] = []
): Promise<{ success: boolean; comment?: QueryComment; message?: string }> => {
  try {
    const response = await api.post<{ comment: QueryComment }>(
      `/client-portal/queries/${queryId}/comments`,
      { comment, attachments }
    );
    return {
      success: response?.success ?? true,
      comment: response?.data?.comment,
      message: response?.message,
    };
  } catch (error: any) {
    console.error("Error adding query comment:", error);
    return {
      success: false,
      message: error?.message || "Failed to add comment",
    };
  }
};

// ─── Client Chat API ──────────────────────────────────────────────────────────

export const getClientChatInfo = async (): Promise<{ slate_id: number; unread_count?: number } | null> => {
  try {
    const response = await api.get<{ slate_id: number; unread_count?: number }>("/client-portal/chat/info");
    return response?.data || null;
  } catch {
    return null;
  }
};

/** Mark our team's messages as read for the client (they're looking at the chat) */
export const markClientChatRead = async (): Promise<void> => {
  try {
    await api.put("/client-portal/chat/mark-read", {});
  } catch (error) {
    console.error("Error marking client chat as read:", error);
  }
};

export const getClientChatMessages = async (
  page = 1,
  since?: string
): Promise<{ messages: ClientChatMessage[]; total: number; pages: number; slate_id: number }> => {
  try {
    const qs = since ? `since=${encodeURIComponent(since)}` : `page=${page}`;
    const response = await api.get<{
      messages: ClientChatMessage[];
      total: number;
      pages: number;
      slate_id: number;
    }>(`/client-portal/chat/messages?${qs}`);
    return {
      messages: response?.data?.messages || [],
      total: response?.data?.total || 0,
      pages: response?.data?.pages || 1,
      slate_id: response?.data?.slate_id || 0,
    };
  } catch {
    return { messages: [], total: 0, pages: 1, slate_id: 0 };
  }
};

export const sendClientChatMessage = async (
  message: string
): Promise<{ success: boolean; message?: ClientChatMessage; slate_id?: number }> => {
  try {
    const response = await api.post<{ message: ClientChatMessage; slate_id: number }>(
      "/client-portal/chat/messages",
      { message }
    );
    return {
      success: response?.success ?? true,
      message: response?.data?.message,
      slate_id: response?.data?.slate_id,
    };
  } catch {
    return { success: false };
  }
};
